import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Star, Award, Users, UserPlus } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { testimonials } from '../../data/mock';
import { useAuth } from '../../context/AuthContext';
import { useContent } from '../../context/ContentContext';

const FELLOWSHIP_IMG = 'https://static.prod-images.emergentagent.com/jobs/fc3829d4-353f-4fc4-b817-cd1ab3ff1600/images/e60244460f42e217e6101902772bedfd45c769c63f4b90c4c2bffc276ce6b97f.jpeg';

const MembershipPage = () => {
  const { c } = useContent();
  const { user } = useAuth();

  return (
    <main className="pt-52">
      {/* Hero Section */}
      <section className="py-20 bg-[#f1e3eb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-3xl">
              <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">
                {c('membership.hero.eyebrow', 'Join Our Community')}
              </span>
              <h1 className="font-serif text-4xl sm:text-5xl text-[#3c4a2a] mt-3 mb-6">
                {c('membership.hero.title', 'Become a Member')}
              </h1>
              <p className="text-lg text-[#3c4a2a]/80 leading-relaxed">
                {c('membership.hero.text', 'Join a community of women dedicated to leadership, service, and empowerment. Your membership supports our programs and connects you with inspiring women across Lincoln County.')}
              </p>
            </div>
            <div className="aspect-[3/2] rounded-2xl overflow-hidden shadow-xl" data-testid="membership-hero-image">
              <img
                src={c('membership.hero.image', FELLOWSHIP_IMG)}
                alt="Women in fellowship, connecting over coffee"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Join Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#bd8cae]/30 flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-[#5d6b3c]" />
              </div>
              <h3 className="font-serif text-xl text-[#3c4a2a] mb-2">{c('membership.connect.title', 'Connect')}</h3>
              <p className="text-[#3c4a2a]/70 text-sm">
                {c('membership.connect.text', 'Build meaningful relationships with women who share your values and commitment to community.')}
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#bd8cae]/30 flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-[#5d6b3c]" />
              </div>
              <h3 className="font-serif text-xl text-[#3c4a2a] mb-2">{c('membership.grow.title', 'Grow')}</h3>
              <p className="text-[#3c4a2a]/70 text-sm">
                {c('membership.grow.text', 'Access leadership development opportunities, workshops, and mentorship programs.')}
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#bd8cae]/30 flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-[#5d6b3c]" />
              </div>
              <h3 className="font-serif text-xl text-[#3c4a2a] mb-2">{c('membership.impact.title', 'Impact')}</h3>
              <p className="text-[#3c4a2a]/70 text-sm">
                {c('membership.impact.text', 'Make a real difference in Lincoln County through service projects and community initiatives.')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Membership Levels */}
      <section className="py-20 bg-[#f1e3eb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl text-[#3c4a2a] mb-4">
              {c('membership.levels.heading', 'Join the Club')}
            </h2>
            <div
              className="mt-4 inline-flex items-center gap-3 bg-[#bd8cae]/20 border border-[#bd8cae]/40 rounded-full px-7 py-4 text-base sm:text-lg text-[#3c4a2a] font-medium"
              data-testid="membership-offline-note"
            >
              <UserPlus className="w-5 h-5 text-[#a3688f] flex-shrink-0" />
              {c('membership.offline.note', "Membership is free and open to all — dues will be determined by the club. Create your free member account today to claim your founding spot.")}
            </div>
          </div>

          {!user && (
            <div className="mt-12 text-center">
              <Link to="/register">
                <Button
                  data-testid="membership-register-btn"
                  className="bg-[#5d6b3c] hover:bg-[#3c4a2a] text-white px-8 py-6 text-lg"
                >
                  Create Your Free Member Account
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Junior Spotlight */}
      <section className="py-20 bg-white" data-testid="junior-spotlight">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <div className="aspect-[3/2] rounded-2xl overflow-hidden shadow-xl">
                <img
                  src={c('membership.junior.image', 'https://static.prod-images.emergentagent.com/jobs/fc3829d4-353f-4fc4-b817-cd1ab3ff1600/images/004ae87992a120d6b425906bef6329f9f3a58b56509c940d76051046e3b424e5.jpeg')}
                  alt="Junior members volunteering together in a community garden"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#9dad84]/30 rounded-full -z-10" />
            </div>
            <div>
              <span className="text-[#bd8cae] text-sm uppercase tracking-wider font-medium">
                {c('membership.junior.eyebrow', 'The Next Generation')}
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl text-[#3c4a2a] mt-3 mb-6">
                {c('membership.junior.heading', 'Junior Spotlight')}
              </h2>
              <p className="text-[#3c4a2a]/80 leading-relaxed mb-8">
                {c('membership.junior.text', "Our Junior Members are young women and students who serve alongside us, build leadership skills early, and bring fresh energy to everything we do. Junior projects count toward school service hours — and toward building the community they'll one day lead.")}
              </p>
              <div className="space-y-4 mb-8">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#bd8cae]/30 flex items-center justify-center flex-shrink-0">
                      <Star className="w-5 h-5 text-[#5d6b3c]" />
                    </div>
                    <div>
                      <p className="font-medium text-[#3c4a2a]">
                        {c(`membership.junior.p${i}.title`, ['Student Service Projects', 'Leadership Mentoring', 'Scholarship & Growth'][i - 1])}
                      </p>
                      <p className="text-sm text-[#3c4a2a]/70">
                        {c(`membership.junior.p${i}.text`, [
                          'School supply drives, community garden days, and literacy read-alongs — earn service hours while making a real difference.',
                          'Every junior is paired with a club member who mentors her in leadership, service, and confidence.',
                          'Workshops, college prep encouragement, and future scholarship opportunities as our club grows.',
                        ][i - 1])}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              <Link to="/register?level=junior">
                <Button
                  data-testid="junior-join-btn"
                  className="bg-[#bd8cae] hover:bg-[#a3688f] text-white px-8 py-5"
                >
                  Join as a Junior Member
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="font-serif text-3xl text-[#3c4a2a] mb-4">
              What Our Members Say
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="testimonial-card border-0 shadow-md p-8">
                <div className="quote-mark leading-none mb-2">“</div>
                <p className="text-[#3c4a2a]/80 italic mb-6 leading-relaxed">
                  {testimonial.quote}
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full overflow-hidden mr-4">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <p className="font-medium text-[#3c4a2a]">{testimonial.name}</p>
                    <p className="text-sm text-[#3c4a2a]/60">{testimonial.title}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Questions CTA */}
      <section className="py-16 bg-[#5d6b3c]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl text-white mb-4">
            Have Questions About Membership?
          </h2>
          <p className="text-white/90 mb-8">
            We'd love to tell you more about the benefits of joining our community.
          </p>
          <Link to="/contact">
            <Button className="bg-[#bd8cae] hover:bg-[#bd8cae] text-[#3c4a2a] px-8">
              Contact Us
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
};

export default MembershipPage;
